/**
 * Workspace API client — PostgreSQL-backed workspace config
 */
import { request, fetchRaw, type RequestIdentity } from '@papan/api-client'
import { filesApi } from '@papan/files/client'
import { loadCurrentPermission, loadCurrentOpensAsProject } from '@papan/files/client/queries'
import { workspaceAccessOutcome, type WorkspaceAccessOutcome } from '@papan/types'
import { isProjectFolder, stripProjectFolderExt } from '@papan/types'
import type { WorkspaceConfig, WorkspaceLayout, FileOverride } from '../types'

/**
 * Fetch a workspace endpoint with a per-attempt timeout so a STALLED response
 * (latency / packet-loss — reproduced on contended stagetest) can't wedge the
 * workspace UI forever on its "Loading workspace..." / "Creating workspace..."
 * spinner. The plain api-client fetch has no timeout, and a stall throws
 * nothing, so no retry layer can otherwise recover it.
 *
 * Each attempt is bounded by AbortSignal.timeout; a stalled attempt aborts
 * (DOMException 'TimeoutError') and the call retries — bounded to 4 attempts.
 * Also retries the caller-named transient HTTP statuses (e.g. a transient
 * post-create 404, issue #86). Terminal errors propagate immediately.
 *
 * Uses fetchRaw, not the deduped request(), so an abort can't poison the
 * shared GET cache or cross-abort another caller.
 */
async function fetchWorkspaceJson<T>(
  path: string,
  init: RequestInit = {},
  opts: { retryStatus?: (status: number) => boolean } = {},
): Promise<T> {
  const ATTEMPT_TIMEOUT_MS = 10_000
  let lastErr: unknown
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      const res = await fetchRaw(path, { ...init, signal: AbortSignal.timeout(ATTEMPT_TIMEOUT_MS) })
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText })) as { error?: string }
        const e = new Error(body.error || 'Request failed') as Error & { status?: number }
        e.status = res.status
        throw e
      }
      return await res.json() as T
    } catch (err) {
      lastErr = err
      const status = (err as { status?: number })?.status
      const isStall = err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError')
      const retryable = isStall || (status !== undefined && (opts.retryStatus?.(status) ?? false))
      if (!retryable || attempt === 3) throw err
      await new Promise(resolve => setTimeout(resolve, 200 * (attempt + 1)))
    }
  }
  throw lastErr // unreachable — the loop always returns or throws
}

/**
 * HTTP statuses worth retrying as TRANSIENT on a workspace resolve (#675):
 * 401 (the post-navigation auth window, before the session is API-effective),
 * 408/429 (contention), and any 5xx. A 404 is NOT here — it is transient only
 * for the `by-folder` GET (the post-create read-your-writes race, #86), passed
 * in explicitly there; for the `open-folder` POST authority a 404/403/400 is a
 * definitive negative. 403/400/other-4xx are definitive everywhere — a retry
 * only delays the correct answer.
 */
function isTransientResolveStatus(status: number): boolean {
  if (status === 401 || status === 408 || status === 429) return true
  return status >= 500 && status <= 599
}

/**
 * After the resolve AUTHORITY (openFolder) has exhausted its own transient
 * retries, is the failure a DEFINITIVE negative — the workspace is genuinely
 * gone or inaccessible, so the terminal "Project not found / may have been
 * deleted or moved to bin" copy is the truthful answer — as opposed to a
 * transient the retries could not outlast (which must surface the recoverable
 * "Couldn't load / Retry" state instead)?
 *
 * open-folder returns 403 for both "no permission" and "folder gone" (a missing
 * folder resolves to `none`), and never 404, so a status-bearing non-transient
 * error is the definitive case. A network drop / stall carries no `.status` and
 * is transient. This is the resolver's terminal-vs-recoverable switch (#675) —
 * the mirror of the boot-list `isTransientLoadError` decision, kept pure and
 * testable rather than buried in the App.tsx effect.
 */
export function isDefinitiveResolveFailure(err: unknown): boolean {
  const status = (err as { status?: number } | null | undefined)?.status
  if (status === undefined) return false // network / stall → transient, recoverable
  return !isTransientResolveStatus(status)
}

export interface WorkspaceListItem {
  id: string
  folderId: string
  name: string
  path: string
  updatedAt: string | null
}

export const workspaceApi = {
  /**
   * List all workspaces for the current tenant
   */
  async list(): Promise<WorkspaceListItem[]> {
    const res = await request<{ workspaces: WorkspaceListItem[] }>('/api/workspaces')
    return res.workspaces
  },

  /**
   * List recent workspaces (per-email for orgs), limit 10
   */
  async listRecent(): Promise<WorkspaceListItem[]> {
    const res = await request<{ workspaces: WorkspaceListItem[] }>('/api/workspaces/recent')
    return res.workspaces
  },

  /**
   * Check if the user can create a new workspace (individual or organization scope)
   */
  async checkNewWorkspacePermission(scope: 'individual' | 'organization'): Promise<{ canCreate: boolean }> {
    return request<{ canCreate: boolean }>(`/api/workspaces/can-create?scope=${scope}`)
  },

  /**
   * List open workspaces (sidebar tabs), ordered by openOrder
   */
  async listOpen(): Promise<Array<{ id: string; folderId: string; name: string; path?: string }>> {
    const res = await request<{ workspaces: Array<{ id: string; folderId: string; name: string; path?: string }> }>('/api/workspaces/open')
    return res.workspaces
  },

  /** Mark a workspace as open in the sidebar */
  async markOpen(workspaceId: string): Promise<void> {
    await request(`/api/workspaces/${encodeURIComponent(workspaceId)}/open`, { method: 'PATCH' })
  },

  /** Mark a workspace as closed in the sidebar */
  async markClosed(workspaceId: string): Promise<void> {
    await request(`/api/workspaces/${encodeURIComponent(workspaceId)}/close`, { method: 'PATCH' })
  },

  /** Reorder open workspaces */
  async reorderOpen(orderedIds: string[]): Promise<void> {
    await request('/api/workspaces/reorder-open', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderedIds }),
    })
  },

  /**
   * Find the best workspace for a file (smart open)
   */
  async findForFile(fileId: string, openWorkspaceFolderIds?: string[]): Promise<{
    workspaceFolderId: string | null
    parentFolderId: string | null
    parentFolderName: string | null
  }> {
    return request('/api/workspaces/find-for-file', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileId, openWorkspaceFolderIds }),
    })
  },

  /**
   * Load workspace config by its folder ID (.workpapan folder)
   */
  async loadConfig(folderId: string): Promise<WorkspaceConfig & { callerPermission?: string }> {
    // This GET gates `configLoaded` in useWorkspace.load(): until it resolves,
    // WorkspaceView is stuck on "Loading workspace...". Two transient failures
    // must NOT wedge it permanently:
    //
    //   - 404: a workspace created moments ago can transiently 404 on a
    //     contended / proxied backend (the create write isn't yet observable
    //     on the path this read takes). The SPA must NOT render a terminal
    //     "Project not found" on the first such 404 (issue #86's secondary,
    //     product-side item). A genuinely-missing workspace still resolves to
    //     not-found within the small retry window.
    //   - stall: the request can hang under latency/packet-loss with no
    //     server response. The plain api-client fetch has no timeout, so a
    //     single stalled response would wedge the workspace on the spinner
    //     FOREVER. Each attempt is bounded by AbortSignal.timeout; a stalled
    //     attempt aborts and retries instead of hanging. (We use fetchRaw, not
    //     the deduped request(), so the abort can't poison the shared GET cache
    //     or cross-abort another caller.)
    //
    // Bounded to 4 attempts. #675 broadened the retry from 404-only to the whole
    // transient set (401 auth window / 408 / 429 / any 5xx), so a transient blip
    // on THIS read self-heals here instead of dropping to the openFolder fallback
    // (and re-running the first-open lockdown) on every non-404 hiccup. A real
    // 403/400 still propagates immediately to that fallback, which is the
    // create-or-open authority. 404 stays retried (the post-create race, #86).
    const data = await fetchWorkspaceJson<{ workspace: WorkspaceConfig; callerPermission?: string }>(
      `/api/workspaces/by-folder/${encodeURIComponent(folderId)}`,
      {},
      { retryStatus: (s) => s === 404 || isTransientResolveStatus(s) },
    )
    return { ...data.workspace, callerPermission: data.callerPermission }
  },

  /**
   * Batch relink missing folders (atomic cascade + validate + write)
   */
  async relinkFolders(
    workspaceId: string,
    relinks: Array<{ linkIndex: number; newTargetFolderId: string }>,
    removals: number[],
  ): Promise<WorkspaceConfig> {
    const res = await request<{ workspace: WorkspaceConfig }>(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/relink`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ relinks, removals }),
      }
    )
    return res.workspace
  },

  /**
   * Save workspace config (full sync — replaces layout, links, tabs)
   */
  async saveConfig(
    workspaceId: string,
    config: {
      name?: string
      layout?: WorkspaceLayout
      links?: Array<{ id?: string; targetFolderId: string; alias?: string | null; sortOrder?: number; path?: string; permission?: string }>
      tabs?: Array<{ id?: string; fileId: string; sortOrder?: number }>
    }
  ): Promise<WorkspaceConfig> {
    const res = await request<{ workspace: WorkspaceConfig }>(
      `/api/workspaces/${encodeURIComponent(workspaceId)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      }
    )
    return res.workspace
  },

  /**
   * Create a new workspace for a folder
   */
  async createWorkspace(folderId: string, name: string): Promise<WorkspaceConfig> {
    const res = await request<{ workspace: WorkspaceConfig }>('/api/workspaces', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ folderId, name }),
    })
    return res.workspace
  },

  /**
   * Create a new untitled workspace.
   * @param scope - 'individual' (user's folder) or 'organization' (shared folder). Default: individual for orgs, shared for personal.
   */
  async createUntitled(scope?: 'individual' | 'organization'): Promise<{ workspace: WorkspaceConfig; folderId: string }> {
    // Gates EmptyWorkspaceView's "Creating workspace..." spinner. Same
    // stall-recovery as loadConfig: a stalled POST would otherwise wedge the
    // spinner forever (no fetch timeout). Retry is safe because the server
    // reuses an existing empty Untitled workspace (routes.ts), so a retry
    // after a stalled attempt reuses the one that attempt created rather than
    // proliferating duplicates.
    return fetchWorkspaceJson<{ workspace: WorkspaceConfig; folderId: string }>(
      '/api/workspaces/create-untitled',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope }),
      },
    )
  },

  /**
   * Open (or create) a workspace for a regular folder.
   * Uses deterministic ws-{folderId} ID so repeated calls are idempotent.
   *
   * This is the AUTHORITY the per-folder resolver falls to when loadConfig
   * rejects (App.tsx). Before #675 it used the plain request() — no stall
   * timeout, no retry — so a transient blip here (after any blip on the GET)
   * dropped straight to markUnresolved → the terminal "Project not found" copy,
   * intermittently and stickily. It now uses the same stall-guarded, bounded
   * transient retry as loadConfig/createUntitled. Retry is safe because the
   * server keys on the deterministic ws-{folderId}/wsi_ id and reuses the row,
   * so a retry after a stalled/5xx attempt re-opens the SAME workspace rather
   * than proliferating duplicates. A definitive 403/404/400 is NOT retried — it
   * propagates immediately so the resolver can render the terminal state fast.
   */
  async openFolder(folderId: string, folderName: string): Promise<{ workspace: WorkspaceConfig; folderId: string }> {
    return fetchWorkspaceJson<{ workspace: WorkspaceConfig; folderId: string }>(
      '/api/workspaces/open-folder',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folderId, folderName }),
      },
      { retryStatus: isTransientResolveStatus },
    )
  },

  /**
   * Link a folder to an existing workspace (idempotent)
   */
  async addLink(workspaceFolderId: string, targetFolderId: string, opts?: { identity?: RequestIdentity }): Promise<WorkspaceConfig> {
    const res = await request<{ workspace: WorkspaceConfig }>('/api/workspaces/add-link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ workspaceFolderId, targetFolderId }),
      // #1316 review round 2: a step of a pinned batch (`pinnedBatch().opts()`) — sent as its starter
      ...(opts?.identity ? { identity: opts.identity } : {}),
    })
    return res.workspace
  },

  /**
   * Delete a workspace
   */
  async deleteWorkspace(workspaceId: string): Promise<void> {
    await request(`/api/workspaces/${encodeURIComponent(workspaceId)}`, {
      method: 'DELETE',
    })
  },

  /**
   * List file overrides for a workspace
   */
  async listFileOverrides(workspaceId: string): Promise<FileOverride[]> {
    const res = await request<{ overrides: FileOverride[] }>(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/file-overrides`
    )
    return res.overrides
  },

  /**
   * Set a file override (upsert)
   */
  async setFileOverride(
    workspaceId: string,
    fileId: string,
    principal: string,
    permission: 'hidden' | 'r',
  ): Promise<void> {
    await request(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/file-overrides`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileId, principal, permission }),
      }
    )
  },

  /**
   * Remove a file override (restores default full access)
   */
  async removeFileOverride(
    workspaceId: string,
    fileId: string,
    principal: string,
  ): Promise<void> {
    await request(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/file-overrides`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileId, principal }),
      }
    )
  },

  /**
   * Get user preferences for a workspace (model, settings including recentFiles)
   */
  async getPreferences(workspaceId: string): Promise<{ modelId: string | null; settings: Record<string, unknown> | null }> {
    return request(`/api/workspaces/${encodeURIComponent(workspaceId)}/preferences`)
  },

  /**
   * Save recent files list to user preferences
   */
  async saveRecentFiles(workspaceId: string, recentFiles: Array<{ fileId: string; openedAt: number }>): Promise<void> {
    // Read existing settings first to avoid overwriting other fields
    const existing = await request<{ settings: Record<string, unknown> | null }>(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/preferences`
    )
    const merged = { ...(existing.settings || {}), recentFiles }
    await request(`/api/workspaces/${encodeURIComponent(workspaceId)}/preferences`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ settings: merged }),
    })
  },
}

/** Check if a folder name indicates a workspace (project folder: `.project` / legacy `.workpapan`) */
export function isWorkpapanFolder(name: string): boolean {
  return isProjectFolder(name.replace(/\/$/, ''))
}

/** Extract display name from workspace folder name */
export function getWorkspaceName(folderName: string): string {
  return stripProjectFolderExt(folderName)
}

export type { FolderWorkspaceAccess } from '@papan/types'
import type { FolderWorkspaceAccess } from '@papan/types'

/** Check workspace availability for a folder (both individual and org scopes) */
export async function getFolderWorkspaceAccess(folderId: string): Promise<{
  individual: FolderWorkspaceAccess
  organization: FolderWorkspaceAccess
}> {
  return request(`/api/workspaces/folder-access/${folderId}`)
}

/**
 * The live access re-check of an open workspace (desktop `WorkspaceView` and the mobile workspace —
 * I262 / #1260 / #1406): the folder's level, read as of NOW, and — only for a 'hidden' level — whether
 * it still opens AS A PROJECT (a hallway project whose config the viewer reads stays). `null` when an
 * answer was superseded by a later access change (that change's own reaction asks again). A revoke
 * (403 / 404 on the folder, or 'hidden' where the project rule says no) leaves; a failed CHECK (a 429,
 * a 5xx, the network — on either read) stays.
 */
export async function loadWorkspaceAccess(folderId: string): Promise<WorkspaceAccessOutcome | null> {
  let permission: string | null
  try {
    permission = await loadCurrentPermission(folderId)
  } catch (error) {
    return workspaceAccessOutcome({ error })
  }
  if (permission === null) return null
  if (permission !== 'hidden') return workspaceAccessOutcome({ permission })
  let opensAsProject: boolean | null | undefined
  try {
    opensAsProject = await loadCurrentOpensAsProject(folderId)
  } catch {
    return { kind: 'stay' } // a failed check, never a revoke (#1260)
  }
  if (opensAsProject === null) return null
  return workspaceAccessOutcome({ permission, opensAsProject })
}

/**
 * #1406 I3 — after the project routes REFUSED a folder as a project (definitively: open-folder's
 * 403, by-folder's 404), may the person still walk into it as a plain folder in their drive — a
 * hallway, a shell, or a readable project whose config they can't read (owner, 2026-10-26: "a
 * project you can't read the config of is a plain folder to you")? Every page that lands on
 * `/workspace/<id>` — desktop and mobile, whichever button sent the person there — asks this and
 * browses `/folder/<id>` on `true`, instead of telling them the project "may have been deleted".
 *
 * Asked through the drive's own `/permission` (any level, `hidden` shells included, means the
 * drive lists and opens it; a folder they can't enter and a missing id both answer 403). `false`
 * on any failure: the not-found answer stays as before — never a guess that sends them somewhere.
 */
export async function opensAsPlainFolder(folderId: string): Promise<boolean> {
  try {
    const permission = await filesApi.getPermission(folderId)
    return typeof permission === 'string' && permission !== 'none'
  } catch {
    return false
  }
}
